import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  FaBookOpen,
  FaChalkboardTeacher,
  FaClipboardCheck,
  FaCloudUploadAlt,
  FaCog,
  FaDatabase,
  FaDoorOpen,
  FaFileContract,
  FaHeadset,
  FaIdCard,
  FaSchool,
  FaSignOutAlt,
  FaTools,
  FaUserGraduate,
  FaUsers,
} from 'react-icons/fa';
import asderLogo from '../../images/asderrLogo.png';

const API = '';

// ── Renk paleti: sınıf seviyesi ──────────────────────────────
const SEVIYE_RENKLER = {
  '5':  { bg: '#78350F', text: '#FEF3C7', border: '#92400E' },
  '6':  { bg: '#14532D', text: '#DCFCE7', border: '#166534' },
  '7':  { bg: '#1E3A8A', text: '#DBEAFE', border: '#1D4ED8' },
  '8':  { bg: '#4C1D95', text: '#EDE9FE', border: '#5B21B6' },
  '9':  { bg: '#831843', text: '#FCE7F3', border: '#9D174D' },
  '10': { bg: '#7C2D12', text: '#FFF7ED', border: '#9A3412' },
  '11': { bg: '#064E3B', text: '#D1FAE5', border: '#065F46' },
  '12': { bg: '#1E3A8A', text: '#EFF6FF', border: '#1D4ED8' },
  'bilinmiyor': { bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' },
};

const DURUM_KONFIG = {
  taslak:          { label: 'Taslak',            color: '#64748B', bg: '#F1F5F9', border: '#E2E8F0' },
  aktif:           { label: '✅ Seçim Açık',      color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
  secim_kapali:    { label: 'Dağıtım Bekliyor',  color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  dagilim_yapildi: { label: '🦋 Dağılım Yapıldı', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
  tamamlandi:      { label: '🏁 Tamamlandı',     color: '#475569', bg: '#F8FAFC', border: '#CBD5E1' },
};

const AKIS_ADIMLARI = {
  taslak: {
    title: 'Hazırlık',
    desc: 'Oturum ve kontenjanlar hazır olduğunda öğrencilerin seçim ekranına açılır.',
    action: 'Yayına Aç',
  },
  aktif: {
    title: 'Seçim Toplanıyor',
    desc: 'Öğrenciler gün seçebilir. Dağıtım oluşturulduğunda seçim otomatik kapanır.',
    action: 'Dağıtımı Oluştur',
  },
  secim_kapali: {
    title: 'Dağıtım Bekliyor',
    desc: 'Seçimler kapalı. Aynı düğme ile sınıf ve salon yerleşimi oluşturulur.',
    action: 'Dağıtımı Oluştur',
  },
  dagilim_yapildi: {
    title: 'Oturma Düzeni Hazır',
    desc: 'Listeler ve oturma düzeni yazdırılabilir.',
    action: 'Oturma Düzenini Gör',
  },
  tamamlandi: {
    title: 'Tamamlandı',
    desc: 'Bu sınav akışı kapatılmış. Gerekirse seçimler yeniden açılabilir.',
    action: 'Oturma Düzenini Gör',
  },
};

const getAkis = (durum) => AKIS_ADIMLARI[durum] || AKIS_ADIMLARI.taslak;

const fmtTarih = (t) => {
  if (!t) return '-';
  return new Date(t).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
};
const fmtGun = (t) => {
  if (!t) return '-';
  return new Date(t).toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long' });
};
const fmtSaat = (s) => s || '-';
const fmtOturum = (g) => `${fmtGun(g?.tarih)}${g?.saat ? ` · ${g.saat}` : ''}${g?.gun_adi ? ` · ${g.gun_adi}` : ''}`;
const sortSecimler = (secimler = []) => [...secimler].sort((a, b) =>
  `${a.ad || ''} ${a.soyad || ''}`.localeCompare(`${b.ad || ''} ${b.soyad || ''}`, 'tr')
);

// ── Global Styles ──────────────────────────────────────────────
const globalStyles = `
  .kelebek-page {
    min-height: calc(100vh - var(--topbar-h));
    color: var(--text-primary);
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  @media (min-width: 640px) {
    .kelebek-page { gap: 26px; }
  }

  @media (min-width: 1024px) {
    .kelebek-page { gap: 28px; }
  }

  .kelebek-page input::placeholder { color: #94A3B8; }
  .modal-open { overflow: hidden; }

  .kelebek-desktop-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: rgba(15, 30, 61, 0.36);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 22px;
  }

  .kelebek-desktop-window {
    width: min(1180px, 96vw);
    min-height: min(760px, 92vh);
    background: #ffffff;
    border: 3px solid #1f2933;
    box-shadow: 18px 22px 0 rgba(0, 0, 0, 0.22), 0 24px 70px rgba(15, 23, 42, 0.34);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .kelebek-desktop-titlebar {
    height: 28px;
    background: linear-gradient(#4b4f55, #242a31);
    color: #eef2f7;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    font-size: 0.88rem;
    font-weight: 900;
    letter-spacing: 0.01em;
  }

  .kelebek-app-mark {
    width: 17px;
    height: 17px;
    border-radius: 2px;
    background: #eaf2ff;
    color: #375a8f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: 900;
  }

  .kelebek-title-close {
    margin-left: auto;
    border: 0;
    background: transparent;
    color: #f8fafc;
    width: 24px;
    height: 24px;
    cursor: pointer;
    font-size: 1.05rem;
    font-weight: 900;
    line-height: 1;
  }

  .kelebek-desktop-body {
    flex: 1;
    padding: 26px 28px 18px;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 22px;
  }

  .kelebek-desktop-top {
    display: grid;
    grid-template-columns: 230px 1fr;
    gap: 22px;
    align-items: start;
  }

  .kelebek-desktop-brand {
    min-height: 144px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #111827;
    text-align: center;
  }

  .kelebek-desktop-brand img {
    width: 82px;
    height: 82px;
    object-fit: contain;
  }

  .kelebek-brand-name {
    font-size: 2.35rem;
    font-weight: 900;
    letter-spacing: -0.03em;
    line-height: 1;
  }

  .kelebek-brand-sub {
    color: #c0392b;
    font-weight: 900;
    font-size: 0.92rem;
  }

  .kelebek-desktop-toolbar {
    display: flex;
    align-items: stretch;
    gap: 8px;
    flex-wrap: wrap;
  }

  .kelebek-toolbar-tile {
    width: 102px;
    min-height: 88px;
    border: 1px solid #cfd5dc;
    background: linear-gradient(#ffffff, #e6e6e6);
    color: #2f343a;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    font-weight: 900;
    font-size: 0.78rem;
    line-height: 1.08;
  }

  .kelebek-toolbar-tile:hover,
  .kelebek-toolbar-tile:focus {
    outline: 2px solid #5b8dd4;
    outline-offset: -2px;
  }

  .kelebek-toolbar-tile svg {
    font-size: 1.7rem;
    color: #4f76ad;
  }

  .kelebek-toolbar-wide {
    width: 142px;
  }

  .kelebek-desktop-title {
    text-align: center;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 1.55rem;
    font-weight: 900;
    color: #111827;
    margin: -4px 0 0;
    letter-spacing: 0.02em;
  }

  .kelebek-desktop-grid {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(3, minmax(160px, 1fr));
    gap: 30px 76px;
    align-content: center;
    justify-items: center;
    padding: 0 110px;
  }

  .kelebek-module {
    width: 190px;
    min-height: 132px;
    border: 0;
    background: transparent;
    color: #222;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 1.08rem;
    font-weight: 900;
    line-height: 1.08;
    text-align: center;
  }

  .kelebek-module:hover .kelebek-module-icon,
  .kelebek-module:focus .kelebek-module-icon {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.14);
  }

  .kelebek-module:focus {
    outline: 2px solid #5b8dd4;
    outline-offset: 4px;
  }

  .kelebek-module-icon {
    width: 88px;
    height: 88px;
    border-radius: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.15s, box-shadow 0.15s;
  }

  .kelebek-module-icon svg {
    font-size: 2.8rem;
  }

  .kelebek-desktop-bottom {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
  }

  .kelebek-bottom-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .kelebek-bottom-action {
    min-width: 168px;
    height: 48px;
    border: 1px solid #cfd5dc;
    background: linear-gradient(#ffffff, #e5e7eb);
    color: #2b3036;
    cursor: pointer;
    font-weight: 900;
    border-radius: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.92rem;
  }

  .kelebek-bottom-action:hover,
  .kelebek-bottom-action:focus {
    outline: 2px solid #5b8dd4;
    outline-offset: -2px;
  }

  @media (max-width: 980px) {
    .kelebek-desktop-window { min-height: 86vh; }
    .kelebek-desktop-body { padding: 18px; overflow: auto; }
    .kelebek-desktop-top { grid-template-columns: 1fr; }
    .kelebek-desktop-brand { min-height: auto; }
    .kelebek-desktop-grid { padding: 0; gap: 24px; }
  }

  @media (max-width: 680px) {
    .kelebek-desktop-overlay { padding: 10px; }
    .kelebek-desktop-grid { grid-template-columns: repeat(2, minmax(130px, 1fr)); }
    .kelebek-module { width: 140px; font-size: 0.92rem; }
    .kelebek-module-icon { width: 72px; height: 72px; }
    .kelebek-desktop-bottom { align-items: stretch; flex-direction: column; }
  }
`;

function InjectStyles() {
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = globalStyles;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);
  return null;
}

// ── Modal ────────────────────────────────────────────────────
function Modal({ title, onClose, children, width = 680 }) {
  useEffect(() => {
    document.body.classList.add('modal-open');
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', handler);
    };
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,30,61,0.48)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '16px 12px', overflowY: 'auto',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#FFFFFF',
        borderRadius: 24,
        width: '100%',
        maxWidth: width,
        border: '1px solid #E2E8F0',
        boxShadow: '0 28px 70px rgba(15,30,61,0.22)',
        display: 'flex', flexDirection: 'column',
        marginTop: 8, marginBottom: 8,
      }}>
        <div style={{
          padding: '18px 20px',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 900, color: '#0F1E3D' }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: '#F8FAFC', border: '1px solid #E2E8F0',
              borderRadius: 10, width: 34, height: 34, cursor: 'pointer',
              color: '#64748B', fontSize: '1rem', display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >✕</button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

function YaziliAyarlaWindow({ onClose, onCreateExam }) {
  const yakinda = (label) => toast(`${label} ekranı bu panelden açılacak şekilde hazırlandı.`);

  const toolbarItems = [
    { label: 'Programlar', icon: <FaUsers />, action: () => toast('Programlar paneli açık') },
    { label: 'Dosya Bakımı', icon: <FaDatabase />, action: () => yakinda('Dosya Bakımı') },
    { label: 'Ayarlar', icon: <FaTools />, action: () => yakinda('Ayarlar') },
    { label: 'Satış-Sözleşme Talebi Oluştur', icon: <FaFileContract />, action: () => yakinda('Satış-Sözleşme Talebi') },
    { label: 'Lisans Talebi Oluştur', icon: <FaIdCard />, action: () => yakinda('Lisans Talebi') },
    { label: 'Destek Talebi Oluştur', icon: <FaHeadset />, action: () => yakinda('Destek Talebi') },
  ];

  const modules = [
    { label: 'Dersler', icon: <FaBookOpen />, color: '#35b9d0', bg: '#e0f7fb', action: () => yakinda('Dersler') },
    { label: 'Sınıflar', icon: <FaSchool />, color: '#7b5ac8', bg: '#f0ebff', action: () => yakinda('Sınıflar') },
    { label: 'Öğrenciler', icon: <FaUserGraduate />, color: '#ef6b7d', bg: '#fff0f3', action: () => yakinda('Öğrenciler') },
    { label: 'Öğrencilere Ders Atanması', icon: <FaClipboardCheck />, color: '#f59e0b', bg: '#fff7df', action: () => yakinda('Öğrencilere Ders Atanması') },
    { label: 'Öğretmenler', icon: <FaChalkboardTeacher />, color: '#4f76ad', bg: '#eaf2ff', action: () => yakinda('Öğretmenler') },
    { label: 'Sınav Salonları', icon: <FaDoorOpen />, color: '#10b981', bg: '#e8fff5', action: () => yakinda('Sınav Salonları') },
    { label: 'Sınav Tanımla', icon: <FaClipboardCheck />, color: '#5b8dd4', bg: '#eef6ff', action: onCreateExam },
    { label: 'Sınavmatik', icon: <FaCog />, color: '#e8334a', bg: '#fff1f2', action: () => yakinda('Sınavmatik') },
    { label: 'Yedekleme', icon: <FaCloudUploadAlt />, color: '#38bdf8', bg: '#e0f2fe', action: () => toast('Yedekleme işlemi için backend bağlantısı gerektiğinde eklenebilir') },
  ];

  return (
    <div className="kelebek-desktop-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="kelebek-desktop-window" role="dialog" aria-modal="true" aria-label="Yazılı Ayarla">
        <div className="kelebek-desktop-titlebar">
          <span className="kelebek-app-mark">K</span>
          <span>Yazılı Ayarla</span>
          <button className="kelebek-title-close" onClick={onClose} aria-label="Kapat">×</button>
        </div>

        <div className="kelebek-desktop-body">
          <div className="kelebek-desktop-top">
            <div className="kelebek-desktop-brand">
              <img src={asderLogo} alt="ASDER Sistem" />
              <div className="kelebek-brand-name">ASDER</div>
              <div className="kelebek-brand-sub">Sınav Yönetimi</div>
            </div>

            <div className="kelebek-desktop-toolbar">
              {toolbarItems.map((item, index) => (
                <button
                  key={item.label}
                  className={`kelebek-toolbar-tile ${index > 2 ? 'kelebek-toolbar-wide' : ''}`}
                  onClick={item.action}
                  type="button"
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
              <button className="kelebek-toolbar-tile" onClick={() => yakinda('İletişim')} type="button">
                <FaHeadset />
                <span>İletişim</span>
              </button>
            </div>
          </div>

          <h2 className="kelebek-desktop-title">ORTAK SINAV-1</h2>

          <div className="kelebek-desktop-grid">
            {modules.map((module) => (
              <button key={module.label} className="kelebek-module" type="button" onClick={module.action}>
                <span className="kelebek-module-icon" style={{ background: module.bg, color: module.color }}>
                  {module.icon}
                </span>
                <span>{module.label}</span>
              </button>
            ))}
          </div>

          <div className="kelebek-desktop-bottom">
            <div className="kelebek-bottom-left">
              <button className="kelebek-bottom-action" type="button" onClick={() => yakinda('Destek Talebi')}>
                <FaHeadset /> Destek Talebi Oluştur
              </button>
            </div>
            <div className="kelebek-bottom-left">
              <button className="kelebek-bottom-action" type="button" onClick={() => toast('Net yedekleme için backend bağlantısı gerektiğinde eklenebilir')}>
                <FaCloudUploadAlt /> Net Yedekleme
              </button>
              <button className="kelebek-bottom-action" type="button" onClick={onClose}>
                <FaSignOutAlt /> Programdan Çıkış
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Input & Label styles ──────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '11px 13px', borderRadius: 12,
  border: '1.5px solid #D8E1EE',
  background: '#FFFFFF', color: '#0F1E3D',
  fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};
const labelStyle = {
  fontSize: '0.72rem', color: '#64748B', fontWeight: 800,
  marginBottom: 5, display: 'block', letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

// ── KelebekForm ───────────────────────────────────────────────
function KelebekForm({ initial, denemeler, onSave, onClose }) {
  const [form, setForm] = useState(initial || {
    ad: '', deneme_id: '', aciklama: '',
    baslangic_tarihi: '', bitis_tarihi: '',
    gunler: [{ tarih: '', saat: '10:00', gun_adi: '', kontenjan: 30 }],
    salon_konfigurasyonu: [{ ad: 'A Salonu', sira_sayisi: 6, sutun_sayisi: 5 }]
  });
  const [loading, setLoading] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addGun = () => setForm(f => ({ ...f, gunler: [...f.gunler, { tarih: '', saat: '10:00', gun_adi: '', kontenjan: 30 }] }));
  const removeGun = (i) => setForm(f => ({ ...f, gunler: f.gunler.filter((_, idx) => idx !== i) }));
  const updateGun = (i, k, v) => setForm(f => ({
    ...f, gunler: f.gunler.map((g, idx) => idx === i ? { ...g, [k]: v } : g)
  }));

  const addSalon = () => setForm(f => ({
    ...f, salon_konfigurasyonu: [...f.salon_konfigurasyonu, { ad: '', sira_sayisi: 5, sutun_sayisi: 5 }]
  }));
  const removeSalon = (i) => setForm(f => ({
    ...f, salon_konfigurasyonu: f.salon_konfigurasyonu.filter((_, idx) => idx !== i)
  }));
  const updateSalon = (i, k, v) => setForm(f => ({
    ...f, salon_konfigurasyonu: f.salon_konfigurasyonu.map((s, idx) => idx === i ? { ...s, [k]: v } : s)
  }));

  const totalKapasit = form.salon_konfigurasyonu.reduce(
    (acc, s) => acc + (parseInt(s.sira_sayisi) || 0) * (parseInt(s.sutun_sayisi) || 0), 0
  );

  const handleSubmit = async () => {
    if (!form.ad || !form.baslangic_tarihi || !form.bitis_tarihi) {
      return toast.error('Ad ve tarihler zorunludur');
    }
    if (form.gunler.some(g => !g.tarih || !g.saat)) return toast.error('Tüm oturumlar için tarih ve saat giriniz');
    setLoading(true);
    try { await onSave(form); } finally { setLoading(false); }
  };

  return (
    <div>
      {/* Temel Bilgiler */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={labelStyle}>Sınav Adı *</label>
            <input style={inputStyle} value={form.ad} onChange={e => setField('ad', e.target.value)} placeholder="Örn: Ocak 2025 TYT Denemesi" />
          </div>
          <div>
            <label style={labelStyle}>Seçim Başlangıç *</label>
            <input type="date" style={inputStyle} value={form.baslangic_tarihi} onChange={e => setField('baslangic_tarihi', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Seçim Bitiş *</label>
            <input type="date" style={inputStyle} value={form.bitis_tarihi} onChange={e => setField('bitis_tarihi', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Sınav Oturumları */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Sınav Oturumları</label>
          <button onClick={addGun} style={btnStyle('#2563EB', '#EFF6FF', '0.78rem')}>+ Oturum Ekle</button>
        </div>
        {form.gunler.map((g, i) => (
          <div key={i} style={{
            background: '#F8FAFC', border: '1px solid #E2E8F0',
            borderRadius: 16, padding: '14px', marginBottom: 10,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <label style={labelStyle}>Tarih</label>
                <input type="date" style={inputStyle} value={g.tarih} onChange={e => updateGun(i, 'tarih', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Saat</label>
                <input type="time" style={inputStyle} value={g.saat || ''} onChange={e => updateGun(i, 'saat', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Oturum Adı</label>
                <input style={inputStyle} placeholder="1. Oturum / TYT Sabah" value={g.gun_adi} onChange={e => updateGun(i, 'gun_adi', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Kontenjan</label>
                <input type="number" style={inputStyle} value={g.kontenjan} min={1} onChange={e => updateGun(i, 'kontenjan', parseInt(e.target.value))} />
              </div>
              {form.gunler.length > 1 && (
                <button onClick={() => removeGun(i)} style={{ ...btnStyle('#F87171', 'rgba(239,68,68,0.1)', '0.8rem'), marginTop: 20, flexShrink: 0 }}>
                  Sil
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Salon Konfigürasyonu */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Salon Konfigürasyonu</label>
            <span style={{ fontSize: '0.72rem', color: '#64748B' }}>
              Toplam: <strong style={{ color: '#60A5FA' }}>{totalKapasit} koltuk</strong>
            </span>
          </div>
          <button onClick={addSalon} style={btnStyle('#34D399', 'rgba(16,185,129,0.1)', '0.78rem')}>+ Salon</button>
        </div>
        {form.salon_konfigurasyonu.map((s, i) => (
          <div key={i} style={{
            background: '#F8FAFC', border: '1px solid #E2E8F0',
            borderRadius: 16, padding: 14, marginBottom: 10,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 8, alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>Salon Adı</label>
                <input style={inputStyle} placeholder="A Salonu" value={s.ad} onChange={e => updateSalon(i, 'ad', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Sıra</label>
                <input type="number" style={inputStyle} value={s.sira_sayisi} min={1} max={20} onChange={e => updateSalon(i, 'sira_sayisi', parseInt(e.target.value))} />
              </div>
              <div>
                <label style={labelStyle}>Sütun</label>
                <input type="number" style={inputStyle} value={s.sutun_sayisi} min={1} max={10} onChange={e => updateSalon(i, 'sutun_sayisi', parseInt(e.target.value))} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontSize: '0.72rem', color: '#64748B' }}>
                {(parseInt(s.sira_sayisi)||0)} × {(parseInt(s.sutun_sayisi)||0)} =
                <strong style={{ color: '#94A3B8' }}> {(parseInt(s.sira_sayisi)||0)*(parseInt(s.sutun_sayisi)||0)} koltuk</strong>
              </span>
              {form.salon_konfigurasyonu.length > 1 && (
                <button onClick={() => removeSalon(i)} style={btnStyle('#F87171', 'rgba(239,68,68,0.1)', '0.75rem')}>Sil</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid #E2E8F0' }}>
        <button onClick={onClose} style={btnStyle('#94A3B8', 'rgba(148,163,184,0.08)')}>İptal</button>
        <button onClick={handleSubmit} disabled={loading} style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          background: loading ? '#CBD5E1' : '#E8334A',
          color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
          fontWeight: 700, fontSize: '0.875rem',
          boxShadow: loading ? 'none' : '0 10px 24px rgba(232,51,74,0.18)',
        }}>
          {loading ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </div>
  );
}

// ── Salon Grid ────────────────────────────────────────────────
function SalonGrid({ salon, yerlesimler }) {
  if (!salon) return null;
  const grid = {};
  for (const y of yerlesimler) {
    if (y.salon_adi === salon.ad) {
      grid[`${y.sira_no}-${y.sutun_no}`] = y;
    }
  }

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        fontSize: '0.78rem', fontWeight: 700, color: '#94A3B8',
        letterSpacing: '0.06em', marginBottom: 12, textTransform: 'uppercase',
      }}>📍 {salon.ad}</div>

      {/* Tahta */}
      <div style={{
        marginBottom: 10, padding: '6px 16px', borderRadius: 6,
        background: '#EFF6FF', border: '1px dashed #BFDBFE',
        textAlign: 'center', fontSize: '0.73rem', color: '#2563EB', width: 'fit-content',
        minWidth: 120,
      }}>
        📋 TAHTA / ÖN
      </div>

      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 4, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <td style={{ width: 26, fontSize: '0.65rem', color: '#475569', textAlign: 'center' }}>#</td>
              {Array.from({ length: salon.sutun_sayisi }, (_, k) => (
                <td key={k} style={{ fontSize: '0.65rem', color: '#64748B', textAlign: 'center', width: 80, paddingBottom: 4 }}>
                  {k + 1}.
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: salon.sira_sayisi }, (_, s) => (
              <tr key={s}>
                <td style={{ fontSize: '0.65rem', color: '#64748B', textAlign: 'center', paddingRight: 4, verticalAlign: 'middle' }}>
                  {s + 1}
                </td>
                {Array.from({ length: salon.sutun_sayisi }, (_, k) => {
                  const ogr = grid[`${s + 1}-${k + 1}`];
                  const renk = ogr ? (SEVIYE_RENKLER[ogr.sinif_seviyesi] || SEVIYE_RENKLER['bilinmiyor']) : null;
                  return (
                    <td key={k} style={{ padding: 0 }}>
                      <div style={{
                        width: 80, height: 46, borderRadius: 6,
                        background: ogr ? renk.bg : '#F8FAFC',
                        border: `1px solid ${ogr ? renk.border : '#E2E8F0'}`,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.65rem', padding: '3px 4px', overflow: 'hidden',
                      }}>
                        {ogr ? (
                          <>
                            <span style={{ fontWeight: 700, color: renk.text, lineHeight: 1.2, textAlign: 'center' }}>
                              {ogr.ad[0]}. {ogr.soyad.length > 8 ? ogr.soyad.slice(0, 8) + '…' : ogr.soyad}
                            </span>
                            <span style={{ color: renk.text, opacity: 0.75, fontSize: '0.6rem' }}>
                              {ogr.sinif_seviyesi}. Sınıf {ogr.sinif_sube || ''}
                            </span>
                          </>
                        ) : (
                          <span style={{ color: '#CBD5E1', fontSize: '0.8rem' }}>—</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── DetayModal ────────────────────────────────────────────────
function DetayModal({ kelebek, onClose, onDurumGuncelle, onDagitimiOlustur, onKontenjanGuncelle, onMisafirKayit }) {
  const [activeTab, setActiveTab] = useState('gunler');
  const [activeGun, setActiveGun] = useState(kelebek?.gunler?.[0]?.id);
  const [yerlesimler, setYerlesimler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kontenjanEdit, setKontenjanEdit] = useState({});
  const [dagilimSonuc, setDagilimSonuc] = useState(null);
  const [misafirForm, setMisafirForm] = useState({ ad: '', soyad: '', telefon: '', sinif_seviyesi: '', sinif_adi: '' });

  const loadYerlesim = useCallback(async (gunId) => {
    try {
      const { data } = await axios.get(`${API}/kelebek/${kelebek.id}/yerlesim?gun_id=${gunId}`);
      if (data.success) setYerlesimler(data.data.liste || []);
    } catch {}
  }, [kelebek.id]);

  useEffect(() => {
    if (activeTab === 'yerlesim' && activeGun) loadYerlesim(activeGun);
  }, [activeTab, activeGun, loadYerlesim]);

  const handleDagil = async () => {
    setYukleniyor(true);
    const sonuc = await onDagitimiOlustur(kelebek);
    setDagilimSonuc(sonuc);
    setYukleniyor(false);
    if (sonuc) setActiveTab('yerlesim');
  };

  const handleAnaAksiyon = async () => {
    if (kelebek.durum === 'taslak') {
      await onDurumGuncelle(kelebek.id, 'aktif');
      return;
    }
    if (['aktif', 'secim_kapali'].includes(kelebek.durum)) {
      await handleDagil();
      return;
    }
    setActiveTab('yerlesim');
    if (activeGun) loadYerlesim(activeGun);
  };

  const handleMisafirKayit = async (gunId) => {
    if (!misafirForm.ad.trim() || !misafirForm.soyad.trim()) return toast.error('Misafir öğrenci adı ve soyadı zorunludur');
    const ok = await onMisafirKayit(kelebek.id, gunId, misafirForm);
    if (ok) setMisafirForm({ ad: '', soyad: '', telefon: '', sinif_seviyesi: '', sinif_adi: '' });
  };

  const printOturumListesi = (gun) => {
    const rows = sortSecimler(gun.secimler || []);
    const html = `
      <html><head><title>${kelebek.ad} - ${fmtOturum(gun)}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#111827}
        h1{font-size:20px;margin:0 0 4px} h2{font-size:15px;margin:0 0 18px;color:#4b5563}
        table{width:100%;border-collapse:collapse} th,td{border:1px solid #d1d5db;padding:8px;text-align:left;font-size:12px}
        th{background:#f3f4f6}.muted{color:#6b7280}.right{text-align:right}
      </style></head><body>
      <h1>${kelebek.ad}</h1><h2>${fmtOturum(gun)} - Alfabetik Öğrenci Listesi</h2>
      <p class="muted">Kontenjan: ${gun.kontenjan} · Kayıtlı: ${rows.length}</p>
      <table><thead><tr><th>#</th><th>Ad Soyad</th><th>Sınıf</th><th>Tür</th><th>İmza</th></tr></thead><tbody>
        ${rows.map((s, i) => `<tr><td class="right">${i + 1}</td><td>${s.ad || ''} ${s.soyad || ''}</td><td>${s.sinif_adi || s.sinif_seviyesi || '-'}</td><td>${s.kayit_tipi === 'misafir' || s.misafir ? 'Misafir' : 'Kurum'}</td><td></td></tr>`).join('')}
      </tbody></table></body></html>`;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return toast.error('Yazdırma penceresi açılamadı');
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const printOturmaDuzeni = () => {
    const gun = kelebek.gunler?.find(g => g.id === activeGun);
    if (!gun) return toast.error('Oturum seçilemedi');
    if (!yerlesimler.length) return toast.error('Yazdırılacak oturma düzeni yok');

    const bySeat = new Map(yerlesimler.map(y => [`${y.salon_adi}-${y.sira_no}-${y.sutun_no}`, y]));
    const salonHtml = salonlar.map(salon => `
      <section class="salon">
        <h2>${salon.ad}</h2>
        <div class="board">TAHTA / ÖN</div>
        <table class="layout">
          <thead>
            <tr>
              <th class="axis">Sıra</th>
              ${Array.from({ length: salon.sutun_sayisi }, (_, i) => `<th>${i + 1}. Koltuk</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${Array.from({ length: salon.sira_sayisi }, (_, rowIdx) => `
              <tr>
                <th class="axis">${rowIdx + 1}. Sıra</th>
                ${Array.from({ length: salon.sutun_sayisi }, (_, colIdx) => {
                  const seat = bySeat.get(`${salon.ad}-${rowIdx + 1}-${colIdx + 1}`);
                  return `<td>
                    ${seat ? `
                      <strong>${seat.ad || ''} ${seat.soyad || ''}</strong>
                      <span>${seat.sinif_adi || seat.sinif_seviyesi || '-'}</span>
                      <small>${rowIdx + 1}. Sıra / ${colIdx + 1}. Koltuk</small>
                    ` : '<em>Boş</em>'}
                  </td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </section>
    `).join('');

    const listeHtml = [...yerlesimler]
      .sort((a, b) => `${a.salon_adi}-${a.sira_no}-${a.sutun_no}`.localeCompare(`${b.salon_adi}-${b.sira_no}-${b.sutun_no}`, 'tr', { numeric: true }))
      .map((y, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${y.ad || ''} ${y.soyad || ''}</td>
          <td>${y.sinif_adi || y.sinif_seviyesi || '-'}</td>
          <td>${y.salon_adi}</td>
          <td>${y.sira_no}. Sıra</td>
          <td>${y.sutun_no}. Koltuk</td>
          <td>${y.masa_kodu || `${y.salon_adi}-${y.sira_no}-${y.sutun_no}`}</td>
        </tr>
      `).join('');

    const html = `
      <html><head><title>${kelebek.ad} - Oturma Düzeni</title>
      <style>
        body{font-family:Arial,sans-serif;color:#111827;padding:22px}
        h1{font-size:20px;margin:0 0 4px} h2{font-size:16px;margin:22px 0 10px}
        .meta{color:#4b5563;margin:0 0 18px;font-size:13px}.board{border:1px dashed #6b7280;border-radius:6px;padding:6px 16px;margin:0 0 8px;width:180px;text-align:center;font-size:12px;color:#374151}
        table{width:100%;border-collapse:collapse}.layout th,.layout td,.list th,.list td{border:1px solid #d1d5db;padding:7px;text-align:left;font-size:11px;vertical-align:top}
        .layout th{background:#f3f4f6;text-align:center}.layout td{height:54px;min-width:105px}.layout strong{display:block;font-size:12px}.layout span,.layout small{display:block;color:#4b5563;margin-top:3px}.axis{width:70px}
        .list th{background:#f3f4f6}.salon{page-break-inside:avoid;margin-bottom:24px}@media print{body{padding:10px}.salon{page-break-inside:avoid}}
      </style></head><body>
      <h1>${kelebek.ad}</h1>
      <p class="meta">${fmtOturum(gun)} · Oturma Düzeni · ${yerlesimler.length} öğrenci</p>
      ${salonHtml}
      <h2>Genel Yer Listesi</h2>
      <table class="list"><thead><tr><th>#</th><th>Öğrenci</th><th>Sınıf</th><th>Salon</th><th>Sıra</th><th>Koltuk</th><th>Masa Kodu</th></tr></thead><tbody>${listeHtml}</tbody></table>
      </body></html>`;

    const win = window.open('', '_blank', 'width=1100,height=800');
    if (!win) return toast.error('Yazdırma penceresi açılamadı');
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const durum = DURUM_KONFIG[kelebek.durum] || DURUM_KONFIG.taslak;
  const akis = getAkis(kelebek.durum);
  const salonlar = Array.isArray(kelebek.salon_konfigurasyonu) ? kelebek.salon_konfigurasyonu : [];

  return (
    <Modal title={`🦋 ${kelebek.ad}`} onClose={onClose} width={920}>
      {/* Başlık Bilgi Şeridi */}
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20,
        padding: 14, borderRadius: 16,
        background: '#F8FAFC', border: '1px solid #E2E8F0',
        alignItems: 'center',
      }}>
        <span style={{
          padding: '4px 12px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700,
          background: durum.bg, color: durum.color, border: `1px solid ${durum.border}`, whiteSpace: 'nowrap',
        }}>
          {durum.label}
        </span>
        <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
          📅 {fmtTarih(kelebek.baslangic_tarihi)} — {fmtTarih(kelebek.bitis_tarihi)}
        </span>
        {kelebek.deneme_adi && (
          <span style={{ fontSize: '0.8rem', color: '#64748B' }}>📝 {kelebek.deneme_adi}</span>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            onClick={handleAnaAksiyon}
            disabled={yukleniyor}
            style={primaryActionStyle(yukleniyor)}
          >
            {yukleniyor ? 'İşleniyor...' : akis.action}
          </button>
          {['secim_kapali', 'dagilim_yapildi', 'tamamlandi'].includes(kelebek.durum) && (
            <button
              onClick={() => {
                const msg = kelebek.durum === 'tamamlandi'
                  ? 'Bu tamamlanan deneme tekrar aktif yapılacak. Öğrenci eklenebilir/seçim yapılabilir hale gelir; gerekirse dağılımı yeniden çalıştırmalısınız. Devam edilsin mi?'
                  : 'Seçimler tekrar açılacak. Devam edilsin mi?';
                if (window.confirm(msg)) onDurumGuncelle(kelebek.id, 'aktif');
              }}
              style={btnStyle('#34D399', 'rgba(5,150,105,0.15)')}
            >
              Seçimleri Yeniden Aç
            </button>
          )}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: 6,
        padding: 14,
        borderRadius: 10,
        background: '#EEF2FF',
        border: '1px solid #C7D2FE',
        marginBottom: 16,
      }}>
        <div style={{ color: '#1D4ED8', fontWeight: 800, fontSize: '0.92rem' }}>{akis.title}</div>
        <div style={{ color: '#64748B', fontSize: '0.82rem', lineHeight: 1.45 }}>{akis.desc}</div>
      </div>

      {/* Dağılım Sonucu */}
      {dagilimSonuc && (
        <div style={{
          padding: 14, borderRadius: 14, background: '#EFF6FF',
          border: '1px solid #BFDBFE', marginBottom: 16,
        }}>
          <div style={{ fontWeight: 800, color: '#2563EB', marginBottom: 8, fontSize: '0.9rem' }}>
            🦋 Dağılım Tamamlandı
          </div>
          {Object.values(dagilimSonuc).map((d, i) => (
            <div key={i} style={{ fontSize: '0.82rem', color: '#1D4ED8', marginBottom: 4 }}>
              📅 {fmtTarih(d.gun_tarih)}: {d.yerlestirilen}/{d.toplam_ogrenci} yerleştirildi
              {d.yerlestirilemeyen > 0 && (
                <span style={{ color: '#E8334A' }}> ({d.yerlestirilemeyen} yerleştirilemedi)</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Sekmeler */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '1px solid #E2E8F0', overflowX: 'auto' }}>
        {[['gunler', '📅 Günler & Kayıtlar'], ['yerlesim', '🗺️ Oturma Düzeni']].map(([t, label]) => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            padding: '8px 16px', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer',
            fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap',
            background: activeTab === t ? '#EEF2FF' : 'transparent',
            color: activeTab === t ? '#2563EB' : '#64748B',
            borderBottom: activeTab === t ? '2px solid #2563EB' : '2px solid transparent',
          }}>{label}</button>
        ))}
      </div>

      {/* Günler & Kayıtlar */}
      {activeTab === 'gunler' && (
        <div>
          {kelebek.gunler?.map(g => {
            const doluluk = g.kontenjan > 0 ? (g.mevcut_kayit / g.kontenjan) * 100 : 0;
            const editingKontenjan = kontenjanEdit[g.id];
            return (
              <div key={g.id} style={{
                marginBottom: 14, borderRadius: 18,
                border: '1px solid #E2E8F0',
                background: '#FFFFFF', overflow: 'hidden',
                boxShadow: '0 6px 18px rgba(15,30,61,0.05)',
              }}>
                <div style={{
                  padding: '12px 16px',
                  background: '#F8FAFC',
                  borderBottom: '1px solid #E2E8F0',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 800, color: '#0F1E3D', fontSize: '0.9rem' }}>
                        {fmtOturum(g)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: 2 }}>
                        {g.secimler?.length || g.mevcut_kayit || 0} / {g.kontenjan} öğrenci · Saat {fmtSaat(g.saat)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {editingKontenjan !== undefined ? (
                        <>
                          <input
                            type="number" min={1}
                            value={editingKontenjan}
                            onChange={e => setKontenjanEdit(prev => ({ ...prev, [g.id]: parseInt(e.target.value) }))}
                            style={{ width: 70, padding: '5px 8px', borderRadius: 8, border: '1px solid #D8E1EE', background: '#FFFFFF', color: '#0F1E3D', fontSize: '0.82rem' }}
                          />
                          <button onClick={async () => {
                            await onKontenjanGuncelle(g.id, editingKontenjan);
                            setKontenjanEdit(prev => { const n = { ...prev }; delete n[g.id]; return n; });
                          }} style={btnStyle('#34D399', 'rgba(5,150,105,0.15)', '0.78rem')}>✓</button>
                          <button onClick={() => setKontenjanEdit(prev => { const n = { ...prev }; delete n[g.id]; return n; })}
                            style={btnStyle('#F87171', 'rgba(239,68,68,0.12)', '0.78rem')}>✕</button>
                        </>
                      ) : (
                        <button onClick={() => setKontenjanEdit(prev => ({ ...prev, [g.id]: g.kontenjan }))}
                          style={btnStyle('#2563EB', '#EFF6FF', '0.75rem')}>
                          ✏️ {g.kontenjan}
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ width: '100%', height: 7, borderRadius: 999, background: '#E2E8F0', overflow: 'hidden', marginTop: 10 }}>
                    <div style={{
                      height: '100%', borderRadius: 3, transition: 'width 0.4s',
                      width: `${Math.min(100, doluluk)}%`,
                      background: doluluk >= 100 ? '#EF4444' : doluluk >= 80 ? '#F59E0B' : '#10B981',
                    }} />
                  </div>
                </div>

                <div style={{ padding: '12px 16px', borderBottom: '1px solid #F1F5F9' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 10 }}>
                    <input style={inputStyle} placeholder="Misafir adı" value={misafirForm.ad} onChange={e => setMisafirForm(f => ({ ...f, ad: e.target.value }))} />
                    <input style={inputStyle} placeholder="Misafir soyadı" value={misafirForm.soyad} onChange={e => setMisafirForm(f => ({ ...f, soyad: e.target.value }))} />
                    <input style={inputStyle} placeholder="Telefon" value={misafirForm.telefon} onChange={e => setMisafirForm(f => ({ ...f, telefon: e.target.value }))} />
                    <input style={inputStyle} placeholder="Sınıf / Seviye" value={misafirForm.sinif_adi} onChange={e => setMisafirForm(f => ({ ...f, sinif_adi: e.target.value, sinif_seviyesi: e.target.value.replace(/\D/g, '').slice(0, 2) }))} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => handleMisafirKayit(g.id)} style={btnStyle('#34D399', 'rgba(5,150,105,0.15)', '0.78rem')}>+ Misafir Öğrenci Kaydet</button>
                    <button onClick={() => printOturumListesi(g)} style={btnStyle('#60A5FA', 'rgba(37,99,235,0.12)', '0.78rem')}>🖨️ Oturum Listesi Yazdır</button>
                  </div>
                </div>

                {g.secimler?.length > 0 ? (
                  <div style={{ padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {sortSecimler(g.secimler).map(s => {
                      const renk = SEVIYE_RENKLER[s.sinif_seviyesi] || SEVIYE_RENKLER['bilinmiyor'];
                      return (
                        <span key={s.ogrenci_id} style={{
                          padding: '4px 10px', borderRadius: 20, fontSize: '0.75rem',
                          background: renk.bg, color: renk.text, border: `1px solid ${renk.border}`,
                          fontWeight: 600,
                        }}>
                          {s.ad} {s.soyad}
                          <span style={{ opacity: 0.65, fontWeight: 400 }}> ({s.sinif_adi || s.sinif_seviyesi})</span>
                          {(s.kayit_tipi === 'misafir' || s.misafir) && <span style={{ marginLeft: 4, color: '#FBBF24' }}>Misafir</span>}
                          {s.durum === 'yerlestirildi' && <span style={{ marginLeft: 4, color: '#34D399' }}>✓</span>}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '12px 16px', color: '#475569', fontSize: '0.8rem', fontStyle: 'italic' }}>
                    Henüz seçim yapılmamış
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Oturma Düzeni */}
      {activeTab === 'yerlesim' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {kelebek.gunler?.map(g => (
                <button key={g.id} onClick={() => { setActiveGun(g.id); loadYerlesim(g.id); }}
                  style={{
                    padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    fontSize: '0.8rem', fontWeight: 600,
                    background: activeGun === g.id ? '#EFF6FF' : '#F8FAFC',
                    color: activeGun === g.id ? '#2563EB' : '#64748B',
                    outline: activeGun === g.id ? '1px solid #BFDBFE' : '1px solid #E2E8F0',
                  }}>
                  {fmtOturum(g)}
                </button>
              ))}
            </div>
            <button onClick={printOturmaDuzeni} style={btnStyle('#60A5FA', 'rgba(37,99,235,0.12)')}>
              🖨️ Oturma Düzeni Yazdır
            </button>
          </div>

          {yerlesimler.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#475569' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🦋</div>
              <div style={{ fontSize: '0.9rem' }}>Henüz dağılım yapılmamış.</div>
              {kelebek.durum === 'secim_kapali' && (
                <div style={{ marginTop: 10, color: '#2563EB', fontSize: '0.82rem' }}>
                  "Dağıtımı Oluştur" butonuna basarak yerleşimi hazırlayın.
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                {Object.entries(SEVIYE_RENKLER).filter(([k]) => k !== 'bilinmiyor').map(([seviye, renk]) => {
                  const var_ = yerlesimler.some(y => y.sinif_seviyesi === seviye);
                  if (!var_) return null;
                  return (
                    <span key={seviye} style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem',
                      background: renk.bg, color: renk.text, border: `1px solid ${renk.border}`,
                      fontWeight: 600,
                    }}>
                      {seviye}. Sınıf
                    </span>
                  );
                })}
              </div>
              {salonlar.map(s => <SalonGrid key={s.ad} salon={s} yerlesimler={yerlesimler} />)}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function btnStyle(color, bg, fontSize = '0.82rem') {
  return {
    padding: '7px 14px', borderRadius: 12,
    border: `1px solid ${color}40`,
    background: bg, color: color,
    cursor: 'pointer', fontWeight: 800, fontSize,
    whiteSpace: 'nowrap',
  };
}

function primaryActionStyle(disabled = false) {
  return {
    padding: '9px 16px',
    borderRadius: 8,
    border: '1px solid rgba(232,51,74,0.35)',
    background: disabled ? '#CBD5E1' : '#E8334A',
    color: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 800,
    fontSize: '0.84rem',
    whiteSpace: 'nowrap',
    boxShadow: disabled ? 'none' : '0 10px 24px rgba(232,51,74,0.18)',
  };
}

// ── Ana Sayfa ─────────────────────────────────────────────────
export default function KelebekYonetimiPage() {
  const [kelebekler, setKelebekler] = useState([]);
  const [denemeler, setDenemeler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showYaziliAyarla, setShowYaziliAyarla] = useState(false);
  const [detayKelebek, setDetayKelebek] = useState(null);
  const [filtre, setFiltre] = useState('tumu');
  const [islemdeId, setIslemdeId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, d] = await Promise.all([
        axios.get(`${API}/kelebek`),
        axios.get(`${API}/denemeler`),
      ]);
      if (k.data.success) setKelebekler(k.data.data);
      if (d.data.success) setDenemeler(d.data.data || []);
    } catch {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    const { data } = await axios.post(`${API}/kelebek`, form);
    if (data.success) {
      toast.success('Kelebek kaydı oluşturuldu!');
      setShowForm(false);
      load();
    } else {
      toast.error(data.message);
    }
  };

  const handleDurumGuncelle = async (id, durum) => {
    try {
      const { data } = await axios.put(`${API}/kelebek/${id}/durum`, { durum });
      if (data.success) {
        toast.success('Durum güncellendi');
        load();
        if (detayKelebek?.id === id) {
          const { data: d2 } = await axios.get(`${API}/kelebek/${id}`);
          if (d2.success) setDetayKelebek(d2.data);
        }
      }
    } catch { toast.error('Hata oluştu'); }
  };

  const refreshDetay = async (id) => {
    if (detayKelebek?.id !== id) return;
    const { data } = await axios.get(`${API}/kelebek/${id}`);
    if (data.success) setDetayKelebek(data.data);
  };

  const handleKontenjanGuncelle = async (gunId, kontenjan) => {
    try {
      const { data } = await axios.put(`${API}/kelebek/gun/${gunId}/kontenjan`, { kontenjan });
      if (data.success) {
        toast.success('Kontenjan güncellendi');
        if (detayKelebek) {
          const { data: d2 } = await axios.get(`${API}/kelebek/${detayKelebek.id}`);
          if (d2.success) setDetayKelebek(d2.data);
        }
      }
    } catch { toast.error('Hata'); }
  };

  const handleMisafirKayit = async (kelebekId, gunId, form) => {
    try {
      const { data } = await axios.post(`${API}/kelebek/${kelebekId}/misafir-kayit`, {
        gun_id: gunId,
        ...form,
      });
      if (data.success) {
        toast.success(data.message || 'Misafir öğrenci oturuma kaydedildi');
        load();
        const { data: d2 } = await axios.get(`${API}/kelebek/${kelebekId}`);
        if (d2.success) setDetayKelebek(d2.data);
        return true;
      }
      toast.error(data.message || 'Misafir kayıt oluşturulamadı');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Misafir kayıt endpointi henüz hazır değil');
    }
    return false;
  };

  const handleDagil = async (id) => {
    try {
      const { data } = await axios.post(`${API}/kelebek/${id}/dagil`);
      if (data.success) {
        toast.success(data.message);
        load();
        const { data: d2 } = await axios.get(`${API}/kelebek/${id}`);
        if (d2.success) setDetayKelebek(d2.data);
        return data.data;
      } else {
        toast.error(data.message);
        return null;
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Hata');
      return null;
    }
  };

  const handleDagitimiOlustur = async (kelebek) => {
    const id = typeof kelebek === 'object' ? kelebek.id : kelebek;
    const mevcut = typeof kelebek === 'object'
      ? kelebek
      : kelebekler.find(k => k.id === id) || detayKelebek;

    if (!mevcut) return null;
    if (mevcut.durum === 'taslak') {
      toast.error('Önce yayına açın');
      return null;
    }
    if (!['aktif', 'secim_kapali'].includes(mevcut.durum)) {
      if (detayKelebek?.id !== id) handleDetay(id);
      return null;
    }

    const mesaj = mevcut.durum === 'aktif'
      ? 'Seçimler kapatılıp oturma düzeni oluşturulacak. Mevcut yerleşim varsa yenilenir. Devam edilsin mi?'
      : 'Oturma düzeni oluşturulacak. Mevcut yerleşim varsa yenilenir. Devam edilsin mi?';
    if (!window.confirm(mesaj)) return null;

    setIslemdeId(id);
    try {
      if (mevcut.durum === 'aktif') {
        const { data: kapat } = await axios.put(`${API}/kelebek/${id}/durum`, { durum: 'secim_kapali' });
        if (!kapat.success) {
          toast.error(kapat.message || 'Seçimler kapatılamadı');
          return null;
        }
      }
      return await handleDagil(id);
    } finally {
      setIslemdeId(null);
      load();
      refreshDetay(id).catch(() => {});
    }
  };

  const handleDetay = async (id) => {
    try {
      const { data } = await axios.get(`${API}/kelebek/${id}`);
      if (data.success) setDetayKelebek(data.data);
    } catch { toast.error('Detay yüklenemedi'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kelebek kaydı silinsin mi?')) return;
    await axios.delete(`${API}/kelebek/${id}`);
    toast.success('Silindi');
    load();
  };

  const filtreliKelebekler = filtre === 'tumu'
    ? kelebekler
    : kelebekler.filter(k => k.durum === filtre);

  return (
    <>
      <InjectStyles />
      <div className="kelebek-page">
        <div className="section-header animate-fadeInUp" style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 14,
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '2.25rem', fontWeight: 900, color: '#0F1E3D', letterSpacing: '-0.04em' }}>
              🦋 Sınav Oluştur
            </h1>
            <p className="section-subtitle" style={{ margin: '8px 0 0', color: '#64748B', fontSize: '0.98rem' }}>
              Deneme sınavı gün seçimi ve sınıf dağılımı yönetimi
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowYaziliAyarla(true)} style={{
              height: 56, borderRadius: 18, padding: '0 24px',
              border: '1px solid #CBD5E1',
              background: 'linear-gradient(#FFFFFF, #F1F5F9)',
              color: '#0F1E3D',
              boxShadow: '0 10px 24px rgba(15,30,61,0.08)',
              fontWeight: 900, whiteSpace: 'nowrap', flexShrink: 0,
              cursor: 'pointer',
            }}>
              Yazılı Ayarla
            </button>
            <button className="btn btn-primary" onClick={() => setShowForm(true)} style={{
              height: 56, borderRadius: 18, padding: '0 28px',
              boxShadow: '0 10px 24px rgba(232,51,74,0.18)',
              fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              + Sınav Oluştur
            </button>
          </div>
        </div>

        <div className="card animate-fadeInUp" style={{ padding: 14, borderRadius: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            ['tumu', 'Tümü'],
            ...Object.entries(DURUM_KONFIG).map(([k, v]) => [k, v.label])
          ].map(([k, label]) => (
            <button key={k} onClick={() => setFiltre(k)} style={{
              padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
              fontSize: '0.78rem', fontWeight: 800,
              border: `1.5px solid ${filtre === k ? '#E8334A' : '#E2E8F0'}`,
              background: filtre === k ? '#FFF1F2' : '#FFFFFF',
              color: filtre === k ? '#E8334A' : '#64748B',
              transition: 'all 0.15s',
            }}>
              {label}
              <span style={{ marginLeft: 5, opacity: 0.7 }}>
                ({k !== 'tumu' ? kelebekler.filter(x => x.durum === k).length : kelebekler.length})
              </span>
            </button>
          ))}
        </div>

        {/* Liste */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: '#475569', fontSize: '0.9rem' }}>
            ⏳ Yükleniyor...
          </div>
        ) : filtreliKelebekler.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            background: '#FFFFFF', borderRadius: 24,
            border: '1.5px dashed #D8E1EE',
            boxShadow: '0 2px 12px rgba(15,30,61,0.07)',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>🦋</div>
            <div style={{ color: '#475569', fontSize: '0.9rem' }}>Henüz kelebek kaydı yok</div>
            <button onClick={() => setShowForm(true)} style={{
              marginTop: 16, padding: '10px 22px', borderRadius: 12, border: 'none',
              background: '#E8334A', color: '#FFFFFF', cursor: 'pointer', fontWeight: 800,
            }}>İlk kaydı oluştur</button>
          </div>
        ) : (
          <div className="animate-fadeInUp" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
            gap: 24,
          }}>
            {filtreliKelebekler.map(k => {
              const durum = DURUM_KONFIG[k.durum] || DURUM_KONFIG.taslak;
              const akis = getAkis(k.durum);
              const toplamKontenjan = k.gunler?.reduce((a, g) => a + g.kontenjan, 0) || 0;
              const islemde = islemdeId === k.id;
              return (
                <div
                  key={k.id}
                  style={{
                    background: '#FFFFFF', borderRadius: 24,
                    border: '1px solid #E2E8F0',
                    padding: 0, cursor: 'pointer', overflow: 'hidden',
                    boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
                    transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                  }}
                  onClick={() => handleDetay(k.id)}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 14px 32px rgba(15,23,42,0.10)';
                    e.currentTarget.style.borderColor = '#BFDBFE';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '0 10px 28px rgba(15,23,42,0.06)';
                    e.currentTarget.style.borderColor = '#E2E8F0';
                  }}
                >
                  <div style={{ height: 5, background: '#F87171' }} />
                  <div style={{ padding: 22 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 900, color: '#1E293B', fontSize: '1.05rem',
                        marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{k.ad}</div>
                      {k.deneme_adi && (
                        <div style={{ fontSize: '0.75rem', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          📝 {k.deneme_adi}
                        </div>
                      )}
                    </div>
                    <span style={{
                      padding: '5px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 800,
                      background: durum.bg, color: durum.color, border: `1px solid ${durum.border}`,
                      whiteSpace: 'nowrap', marginLeft: 10, flexShrink: 0,
                    }}>
                      {durum.label}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                    {[
                      ['📅', 'Oturum', k.gunler?.length || 0],
                      ['👥', 'Kontenjan', toplamKontenjan],
                      ['✅', 'Kayıtlı', k.toplam_secim || 0],
                    ].map(([icon, lbl, val]) => (
                      <div key={lbl} style={{
                        textAlign: 'center', padding: '10px 4px', borderRadius: 14,
                        background: '#F8FAFC', border: '1px solid #E2E8F0',
                      }}>
                        <div style={{ fontSize: '1rem', marginBottom: 2 }}>{icon}</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#1E293B', lineHeight: 1 }}>{val}</div>
                        <div style={{ fontSize: '0.63rem', color: '#94A3B8', marginTop: 3, fontWeight: 700 }}>{lbl}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: 14 }}>
                    🗓️ {fmtTarih(k.baslangic_tarihi)} — {fmtTarih(k.bitis_tarihi)}
                  </div>
                  <div style={{
                    padding: '10px 12px',
                    borderRadius: 14,
                    background: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    marginBottom: 14,
                  }}>
                    <div style={{ color: '#1E293B', fontWeight: 900, fontSize: '0.8rem', marginBottom: 3 }}>
                      {akis.title}
                    </div>
                    <div style={{ color: '#64748B', fontSize: '0.72rem', lineHeight: 1.35 }}>
                      {akis.desc}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                    {k.gunler?.slice(0, 3).map(g => (
                      <span key={g.id || `${g.tarih}-${g.saat}`} style={{
                        padding: '4px 8px', borderRadius: 20, fontSize: '0.68rem',
                        background: '#EFF6FF', color: '#2563EB',
                        border: '1px solid #BFDBFE',
                      }}>{fmtSaat(g.saat)} · {fmtTarih(g.tarih)}</span>
                    ))}
                    {(k.gunler?.length || 0) > 3 && <span style={{ color: '#64748B', fontSize: '0.7rem', alignSelf: 'center' }}>+{k.gunler.length - 3} oturum</span>}
                  </div>

                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                    {k.durum === 'taslak' && (
                      <button onClick={() => handleDurumGuncelle(k.id, 'aktif')} style={primaryActionStyle(false)}>
                        {akis.action}
                      </button>
                    )}
                    {['aktif', 'secim_kapali'].includes(k.durum) && (
                      <button onClick={() => handleDagitimiOlustur(k)} disabled={islemde} style={primaryActionStyle(islemde)}>
                        {islemde ? 'İşleniyor...' : akis.action}
                      </button>
                    )}
                    <button onClick={() => handleDetay(k.id)} style={btnStyle('#2563EB', '#EFF6FF')}>
                      {['dagilim_yapildi', 'tamamlandi'].includes(k.durum) ? akis.action : 'Detay'}
                    </button>
                    <button onClick={() => handleDelete(k.id)} style={btnStyle('#F87171', 'rgba(239,68,68,0.1)')}>
                      Sil
                    </button>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modaller */}
        {showForm && (
          <Modal title="Yeni Sınav Oluştur" onClose={() => setShowForm(false)} width={720}>
            <KelebekForm denemeler={denemeler} onSave={handleCreate} onClose={() => setShowForm(false)} />
          </Modal>
        )}

        {showYaziliAyarla && (
          <YaziliAyarlaWindow
            onClose={() => setShowYaziliAyarla(false)}
            onCreateExam={() => {
              setShowYaziliAyarla(false);
              setShowForm(true);
            }}
          />
        )}

        {detayKelebek && (
          <DetayModal
            kelebek={detayKelebek}
            onClose={() => setDetayKelebek(null)}
            onDurumGuncelle={handleDurumGuncelle}
            onDagitimiOlustur={handleDagitimiOlustur}
            onKontenjanGuncelle={handleKontenjanGuncelle}
            onMisafirKayit={handleMisafirKayit}
          />
        )}
      </div>
    </>
  );
}
